/* Light/dark, in the header, on every page (D33).
   Defaults to the system preference and remembers an override per device —
   the guest walks out of the Khao Lak sun into a dark ballroom inside an hour and
   should not have to go looking for the switch. */

import { useEffect, useState } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';

const KEY = 'mv.theme';
const listeners = new Set<() => void>();

function read(): ThemeChoice {
  const stored = localStorage.getItem(KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

let choice: ThemeChoice = read();

function apply() {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
}

export function resolvedTheme(): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function setTheme(next: ThemeChoice) {
  choice = next;
  if (next === 'system') localStorage.removeItem(KEY);
  else localStorage.setItem(KEY, next);
  apply();
  listeners.forEach((fn) => fn());
}

/** One tap flips to the opposite of what is currently on screen. */
export function toggleTheme() {
  setTheme(resolvedTheme() === 'dark' ? 'light' : 'dark');
}

export function initTheme() { apply(); }

export function useTheme(): { choice: ThemeChoice; resolved: 'light' | 'dark' } {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', fn);
    return () => { listeners.delete(fn); mq.removeEventListener('change', fn); };
  }, []);
  return { choice, resolved: resolvedTheme() };
}
